import { Button, Card, CardContent, CircularProgress, Grid, Stack, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from '../../../config/dayjs';
import PodUserMultiSelect from './PodUserMultiSelect';
import PickerField from '../../../components/PickerField';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { useMasterUserAllList } from '../../../stores/masterUserStore';
import { useCompanyDataList, useCompanyList } from '../../../stores/companyStore';
import { useState, useEffect } from 'react';
import { useLanguage } from '../../../utils/i18n';

export default function UserActivityFilters({
  activeTab,
  setActiveTab,
  selectedDate,
  setSelectedDate,
  selectedUsers,
  setSelectedUsers,
  loading,
  fetchActivityHistory
}) {
  const { t } = useLanguage();
  const isSearchDisabled = selectedUsers.length === 0 || !selectedDate || loading;

  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const { companyOptions = [] } = useCompanyList();

  useEffect(() => {
    if (!isInitialized && companyOptions.length > 0) {
      const storedCompanyId = localStorage.getItem('companyId');
      if (storedCompanyId) {
        const defaultCompany = companyOptions.find((c) => String(c.value) === String(storedCompanyId));
        if (defaultCompany) {
          setSelectedCompanies([defaultCompany.value]);
        }
      }
      setIsInitialized(true);
    }
  }, [companyOptions, isInitialized]);

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    searchKey: 'name'
  });

  const userPickerParams = usePickerPagination({
    fetchHook: useMasterUserAllList,
    formatOption: (item) => ({
      value: item.id,
      label: item.username || item.name || String(item.id)
    }),
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, state.companyIds || undefined],
    searchKey: 'name',
    companyIds: selectedCompanies.join(',')
  });

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        mb: 2
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack spacing={2.5}>
          {/* TAB SELECTION (Removed) */}

          {/* FILTERS AND ACTIONS */}
          <Grid container spacing={2} alignItems="center">
            {/* DATE PICKER */}

            {/* COMPANY PICKER */}
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <PickerField
                label={t('general.company', 'Company')}
                multiSelect={true}
                {...companyPickerParams}
                selectedItems={companyOptions}
                value={selectedCompanies}
                onChange={(val) => {
                  const ids = Array.isArray(val) ? val : val ? [val] : [];
                  setSelectedCompanies(ids);
                  setSelectedUsers([]); // reset selected users when company changes
                }}
                getOptionId={(opt) => opt.id || opt.value}
                getOptionLabel={(opt) => opt.name || opt.label}
                placeholder={t('userActivity.allCompanies', 'All Companies')}
              />
            </Grid>

            {/* USER MULTI-SELECT: REGULAR USER FOR NON-POD vs POD USER FOR POD */}
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              {activeTab === 'non-pod' ? (
                <PickerField
                  label={t('userActivity.user', 'User')}
                  multiSelect={true}
                  {...userPickerParams}
                  value={selectedUsers}
                  onChange={(val) => {
                    const ids = Array.isArray(val) ? val : val ? [val] : [];
                    setSelectedUsers(ids);
                  }}
                  getOptionId={(opt) => opt.id || opt.value}
                  getOptionLabel={(opt) => opt.username || opt.name || opt.label}
                  placeholder={t('userActivity.selectUsers', 'Select Users...')}
                />
              ) : (
                <PodUserMultiSelect selectedUsers={selectedUsers} onChange={(ids) => setSelectedUsers(ids)} />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <DatePicker
                label={t('general.date', 'Date')}
                format="DD/MM/YYYY"
                value={selectedDate ? dayjs(selectedDate) : null}
                onChange={(newDate) => {
                  if (newDate && dayjs(newDate).isValid()) {
                    setSelectedDate(dayjs(newDate).format('YYYY-MM-DD'));
                  }
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    required: true
                  }
                }}
              />
            </Grid>

            {/* SEARCH BUTTON & HINT */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip
                  title={
                    selectedUsers.length === 0
                      ? t('userActivity.selectAtLeastOneUserToSearch', 'Select at least one user to search for data')
                      : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                      onClick={fetchActivityHistory}
                      disabled={isSearchDisabled}
                      sx={{
                        borderRadius: 2.5,
                        textTransform: 'none',
                        px: 3,
                        py: 0.9,
                        fontWeight: 600
                      }}
                    >
                      {t('general.search', 'Search')}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
              {selectedUsers.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('userActivity.selectAtLeastOneUser', 'Select at least one user.')}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
}
